import { useState, useMemo } from 'react';
import { useQuery } from 'wasp/client/operations';
import { getBulkImportHistory } from 'wasp/client/operations';
import { Clock, CheckCircle, AlertCircle, XCircle, ChevronDown, ChevronUp, Search, ExternalLink } from 'lucide-react';
import { Badge } from '../../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { ImportHistoryDetailDialog } from './ImportHistoryDetailDialog';

type ImportHistoryRecord = {
  id: string;
  importedAt: string | Date;
  importType: string;
  fileName: string;
  totalRows: number;
  createdCount: number;
  updatedCount: number;
  skippedCount: number;
  errorCount: number;
  status: string;
  details: any;
  user: {
    email?: string | null;
    username?: string | null;
    fullName?: string | null;
  } | null;
};

const StatusBadge = ({ status }: { status: string }) => {
  if (status === 'completed') {
    return (
      <Badge variant="default" className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
        <CheckCircle className="h-3 w-3 mr-1" />
        Completed
      </Badge>
    );
  }
  if (status === 'partial') {
    return (
      <Badge variant="default" className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
        <AlertCircle className="h-3 w-3 mr-1" />
        Partial
      </Badge>
    );
  }
  return (
    <Badge variant="destructive">
      <XCircle className="h-3 w-3 mr-1" />
      Failed
    </Badge>
  );
};

const DetailRow = ({ record }: { record: ImportHistoryRecord }) => {
  const details = record.details as any;
  const sheets = details?.sheets || {};
  const sheetNames = Object.keys(sheets);

  if (sheetNames.length === 0) {
    return <p className="text-sm text-muted-foreground">No detail data available.</p>;
  }

  return (
    <div className="space-y-3">
      {sheetNames.map((sheet) => {
        const sheetData = sheets[sheet];
        const rows: any[] = sheetData?.rows || [];
        return (
          <div key={sheet} className="border rounded-md p-3">
            <h5 className="font-medium text-sm mb-2 capitalize">{sheet.replace(/_/g, ' ')}</h5>
            <div className="overflow-auto max-h-48">
              <table className="text-xs w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-1 pr-3">Row</th>
                    <th className="text-left py-1 pr-3">Status</th>
                    <th className="text-left py-1 pr-3">Identifier</th>
                    <th className="text-left py-1">Message</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r: any, i: number) => (
                    <tr key={i} className="border-b last:border-0">
                      <td className="py-1 pr-3">{r.row}</td>
                      <td className="py-1 pr-3">
                        <span className={
                          r.status === 'created' ? 'text-green-600' :
                          r.status === 'updated' ? 'text-blue-600' :
                          r.status === 'error' ? 'text-red-600' :
                          'text-muted-foreground'
                        }>
                          {r.status}
                        </span>
                      </td>
                      <td className="py-1 pr-3">{r.identifier || '—'}</td>
                      <td className="py-1 text-muted-foreground">{r.reason || r.message || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
      {details?.executionOrder && (
        <p className="text-xs text-muted-foreground">
          Execution order: {details.executionOrder.join(' → ')}
        </p>
      )}
    </div>
  );
};

const ImportHistoryTable = () => {
  const { data: records, isLoading, error } = useQuery(getBulkImportHistory);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [detailRecord, setDetailRecord] = useState<ImportHistoryRecord | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');

  // Filtered records
  const filteredRecords = useMemo(() => {
    if (!records) return [];

    return (records as ImportHistoryRecord[]).filter((record) => {
      // Search filter (file name or user)
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesFile = record.fileName.toLowerCase().includes(query);
        const matchesUser =
          record.user?.fullName?.toLowerCase().includes(query) ||
          record.user?.username?.toLowerCase().includes(query) ||
          record.user?.email?.toLowerCase().includes(query);
        if (!matchesFile && !matchesUser) return false;
      }

      // Status filter
      if (statusFilter !== 'all' && record.status !== statusFilter) return false;

      // Type filter
      if (typeFilter !== 'all' && record.importType !== typeFilter) return false;

      return true;
    });
  }, [records, searchQuery, statusFilter, typeFilter]);

  if (isLoading) {
    return <p className="text-sm text-muted-foreground py-4">Loading import history...</p>;
  }

  if (error) {
    return <p className="text-sm text-destructive py-4">Failed to load import history.</p>;
  }

  if (!records || records.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-10 text-muted-foreground">
        <Clock className="h-8 w-8 mb-2 opacity-40" />
        <p className="text-sm">No import history yet.</p>
      </div>
    );
  }

  // Unique import types for filter dropdown
  const importTypes = Array.from(new Set((records as ImportHistoryRecord[]).map(r => r.importType)));

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by file name or user..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="partial">Partial</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
          </SelectContent>
        </Select>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Import Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {importTypes.map((type) => (
              <SelectItem key={type} value={type}>
                {type.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Results count */}
      {filteredRecords.length !== records.length && (
        <p className="text-sm text-muted-foreground">
          Showing {filteredRecords.length} of {records.length} imports
        </p>
      )}

      {filteredRecords.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 text-muted-foreground">
          <Search className="h-8 w-8 mb-2 opacity-40" />
          <p className="text-sm">No imports match your filters.</p>
        </div>
      ) : (
        <div className="overflow-auto">
          <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Date / Time</TableHead>
            <TableHead>Imported By</TableHead>
            <TableHead>File</TableHead>
            <TableHead>Type</TableHead>
            <TableHead className="text-center">Created</TableHead>
            <TableHead className="text-center">Updated</TableHead>
            <TableHead className="text-center">Skipped</TableHead>
            <TableHead className="text-center">Errors</TableHead>
            <TableHead>Status</TableHead>
            <TableHead></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filteredRecords.map((record) => (
            <>
              <TableRow key={record.id}>
                <TableCell className="text-sm whitespace-nowrap">
                  {new Date(record.importedAt).toLocaleString()}
                </TableCell>
                <TableCell className="text-sm">
                  {record.user?.fullName || record.user?.username || record.user?.email || '—'}
                </TableCell>
                <TableCell className="text-sm max-w-[160px] truncate" title={record.fileName}>
                  {record.fileName}
                </TableCell>
                <TableCell className="text-sm">
                  <Badge variant="outline" className="capitalize">
                    {record.importType.replace(/_/g, ' ')}
                  </Badge>
                </TableCell>
                <TableCell className="text-center text-sm text-green-600">{record.createdCount}</TableCell>
                <TableCell className="text-center text-sm text-blue-600">{record.updatedCount}</TableCell>
                <TableCell className="text-center text-sm text-muted-foreground">{record.skippedCount}</TableCell>
                <TableCell className="text-center text-sm text-destructive">{record.errorCount}</TableCell>
                <TableCell><StatusBadge status={record.status} /></TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setExpandedId(expandedId === record.id ? null : record.id)}
                      title="Toggle quick view"
                    >
                      {expandedId === record.id ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDetailRecord(record)}
                      title="Open detailed view"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
              {expandedId === record.id && (
                <TableRow key={`${record.id}-detail`}>
                  <TableCell colSpan={10} className="bg-muted/30 p-4">
                    <DetailRow record={record} />
                  </TableCell>
                </TableRow>
              )}
            </>
          ))}
        </TableBody>
      </Table>
        </div>
      )}

      {/* Detail Dialog */}
      <ImportHistoryDetailDialog
        open={!!detailRecord}
        onOpenChange={(open) => !open && setDetailRecord(null)}
        record={detailRecord}
      />
    </div>
  );
};

export default ImportHistoryTable;



